/**
 * The ONE list of currencies and timezones a family can pick from, shared by BOTH app trees.
 *
 * WHY IT LIVES HERE. `POST /api/auth/signup/family` accepts `timezone` and `currency`
 * (`server/auth/schemas/signup.schemas.js:23-24`) and `server/services/signup.service.js:187-194`
 * writes both onto the tenant row. The legacy signup wizard (`src/pages/FamilySignup.js`) already
 * asked for them, from lists in `src/utils/localeDetection.js` — a path the Lantern app under
 * `app/` cannot import, because its only cross-tree alias is `~shared` (`app/vite.config.js:24`).
 *
 * So the two lists are published here VERBATIM from that legacy file, and
 * `shared/locale-options.match.test.js` fails the day the two disagree. The legacy module is left
 * untouched on purpose: it is the production app, and this change is a development-only feature.
 */

/**
 * What the SERVER falls back to when a signup names neither
 * (`server/services/signup.service.js:188-189`). Published here so the form, the draft and the
 * Review summary cannot disagree about what "unchanged" means.
 */
export const DEFAULT_TIMEZONE = 'America/Edmonton';
export const DEFAULT_CURRENCY = 'USD';

export const TOP_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: '$', flag: '🇨🇦' },
  { code: 'AUD', name: 'Australian Dollar', symbol: '$', flag: '🇦🇺' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: '$', flag: '🇳🇿' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$', flag: '🇲🇽' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', flag: '🇳🇴' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', flag: '🇩🇰' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: '$', flag: '🇸🇬' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: '$', flag: '🇭🇰' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', flag: '🇰🇷' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', flag: '🇵🇱' },
];

/**
 * Common timezones grouped by region. User-friendly names with IANA timezone identifiers.
 */
export const TIMEZONE_GROUPS = [
  {
    region: 'North America',
    timezones: [
      { id: 'America/New_York', name: 'Eastern Time (ET)', cities: 'New York, Toronto, Miami' },
      { id: 'America/Chicago', name: 'Central Time (CT)', cities: 'Chicago, Houston, Dallas' },
      { id: 'America/Denver', name: 'Mountain Time (MT)', cities: 'Denver, Salt Lake City, Calgary' },
      { id: 'America/Los_Angeles', name: 'Pacific Time (PT)', cities: 'Los Angeles, Seattle, Vancouver' },
      { id: 'America/Anchorage', name: 'Alaska Time (AKT)', cities: 'Anchorage' },
      { id: 'Pacific/Honolulu', name: 'Hawaii Time (HST)', cities: 'Honolulu' },
      { id: 'America/Phoenix', name: 'Arizona (no DST)', cities: 'Phoenix' },
      { id: 'America/Edmonton', name: 'Mountain Time - Alberta', cities: 'Edmonton, Calgary' },
      { id: 'America/Toronto', name: 'Eastern Time - Ontario', cities: 'Toronto, Ottawa' },
      { id: 'America/Vancouver', name: 'Pacific Time - BC', cities: 'Vancouver, Victoria' },
      { id: 'America/Winnipeg', name: 'Central Time - Manitoba', cities: 'Winnipeg' },
      { id: 'America/Halifax', name: 'Atlantic Time', cities: 'Halifax' },
      { id: 'America/St_Johns', name: 'Newfoundland Time', cities: "St. John's" },
      { id: 'America/Mexico_City', name: 'Mexico City Time', cities: 'Mexico City, Guadalajara' },
    ]
  },
  {
    region: 'Europe',
    timezones: [
      { id: 'Europe/London', name: 'UK Time (GMT/BST)', cities: 'London, Edinburgh' },
      { id: 'Europe/Paris', name: 'Central European (CET)', cities: 'Paris, Berlin, Rome, Madrid' },
      { id: 'Europe/Athens', name: 'Eastern European (EET)', cities: 'Athens, Helsinki, Bucharest' },
      { id: 'Europe/Moscow', name: 'Moscow Time (MSK)', cities: 'Moscow, St. Petersburg' },
      { id: 'Europe/Dublin', name: 'Ireland Time', cities: 'Dublin' },
      { id: 'Europe/Amsterdam', name: 'Netherlands Time', cities: 'Amsterdam' },
      { id: 'Europe/Stockholm', name: 'Sweden Time', cities: 'Stockholm' },
      { id: 'Europe/Zurich', name: 'Switzerland Time', cities: 'Zurich, Geneva' },
    ]
  },
  {
    region: 'Asia & Pacific',
    timezones: [
      { id: 'Asia/Tokyo', name: 'Japan Time (JST)', cities: 'Tokyo, Osaka' },
      { id: 'Asia/Shanghai', name: 'China Time (CST)', cities: 'Beijing, Shanghai, Hong Kong' },
      { id: 'Asia/Singapore', name: 'Singapore Time (SGT)', cities: 'Singapore' },
      { id: 'Asia/Seoul', name: 'Korea Time (KST)', cities: 'Seoul' },
      { id: 'Asia/Kolkata', name: 'India Time (IST)', cities: 'Mumbai, Delhi, Bangalore' },
      { id: 'Asia/Dubai', name: 'Gulf Time (GST)', cities: 'Dubai, Abu Dhabi' },
      { id: 'Asia/Bangkok', name: 'Indochina Time (ICT)', cities: 'Bangkok, Ho Chi Minh' },
      { id: 'Asia/Jakarta', name: 'Western Indonesia Time', cities: 'Jakarta' },
      { id: 'Australia/Sydney', name: 'Australia Eastern (AEST)', cities: 'Sydney, Melbourne' },
      { id: 'Australia/Perth', name: 'Australia Western (AWST)', cities: 'Perth' },
      { id: 'Australia/Brisbane', name: 'Queensland (no DST)', cities: 'Brisbane' },
      { id: 'Pacific/Auckland', name: 'New Zealand (NZST)', cities: 'Auckland, Wellington' },
    ]
  },
  {
    region: 'South America',
    timezones: [
      { id: 'America/Sao_Paulo', name: 'Brazil Time (BRT)', cities: 'Sao Paulo, Rio' },
      { id: 'America/Argentina/Buenos_Aires', name: 'Argentina Time', cities: 'Buenos Aires' },
      { id: 'America/Santiago', name: 'Chile Time (CLT)', cities: 'Santiago' },
      { id: 'America/Bogota', name: 'Colombia Time (COT)', cities: 'Bogota' },
      { id: 'America/Lima', name: 'Peru Time (PET)', cities: 'Lima' },
    ]
  },
  {
    region: 'Africa & Middle East',
    timezones: [
      { id: 'Africa/Johannesburg', name: 'South Africa Time (SAST)', cities: 'Johannesburg, Cape Town' },
      { id: 'Africa/Cairo', name: 'Egypt Time (EET)', cities: 'Cairo' },
      { id: 'Africa/Lagos', name: 'West Africa Time (WAT)', cities: 'Lagos, Accra' },
      { id: 'Africa/Nairobi', name: 'East Africa Time (EAT)', cities: 'Nairobi, Addis Ababa' },
      { id: 'Asia/Jerusalem', name: 'Israel Time (IST)', cities: 'Tel Aviv, Jerusalem' },
      { id: 'Asia/Riyadh', name: 'Arabia Time (AST)', cities: 'Riyadh, Kuwait' },
    ]
  }
];
